import { useMemo } from "react";
import { type UseInfiniteHitsProps, useInfiniteHits, useInstantSearch } from "react-instantsearch";
import { useSearchTransportError } from "@/hooks/use-searchkit-client";
import { useStableSearchResults } from "@/hooks/use-stable-search-results";
import { getSessionHitsCache, type SearchSessionKey } from "@/lib/search-session-store";

// biome-ignore lint/suspicious/noExplicitAny: react-instantsearch's BaseHit constraint is `Record<string, any>`.
type SearchHit = Record<string, any>;

interface UseSearchResultsOptions {
  /** Persist hits and settled results in the session store so revisits
   *  restore them (including loaded scroll pages) instead of re-running
   *  the search behind a skeleton. */
  sessionKey?: SearchSessionKey;
}

export interface UseSearchResultsOutput<THit> {
  hasMore: boolean;
  hits: THit[];
  isError: boolean;
  isInitialLoading: boolean;
  isLoadingMore: boolean;
  showMore: () => void;
  totalCount: number;
}

// Searchkit swallows transport errors so `status === "error"` rarely fires —
// `useSearchTransportError` reads the transporter-layer flag set in
// `useSearchkitClient`.
export function useSearchResults<THit extends SearchHit = SearchHit>({
  sessionKey,
}: UseSearchResultsOptions = {}): UseSearchResultsOutput<THit> {
  const cache = useMemo(() => (sessionKey ? getSessionHitsCache(sessionKey) : undefined), [sessionKey]);
  // The session cache stores hits opaquely; widen it to this page's hit type.
  const { hits, isLastPage, showMore } = useInfiniteHits<THit>(
    cache ? { cache: cache as NonNullable<UseInfiniteHitsProps<THit>["cache"]> } : undefined,
  );
  const { results, status } = useInstantSearch();
  const isTransportError = useSearchTransportError();
  const isError = status === "error" || isTransportError;

  const stable = useStableSearchResults<THit>({
    hasMore: !isLastPage,
    hits,
    sessionKey,
    status: isError ? "error" : status,
    totalCount: results?.nbHits ?? 0,
  });

  const isLoadingMore = !stable.isInitialLoading && (status === "loading" || status === "stalled");

  return {
    hasMore: stable.hasMore,
    hits: stable.hits,
    isError,
    isInitialLoading: stable.isInitialLoading,
    isLoadingMore,
    showMore,
    totalCount: stable.totalCount,
  };
}
